<script setup lang="ts">
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore'
import { signInWithCustomToken } from 'firebase/auth'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import { firebaseAuth, firestoreDb } from '@/services/firebaseClient'
import chatService from '@/services/chatService'
import { packageRequestService } from '@/services'
import type { PackageCountry } from '@/types'

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChatCategory {
  key: 'hotel' | 'tour_package' | 'visa' | 'field_sales' | 'general_support'
  label: string
  icon: string
}

interface ReplyRef {
  id: string
  message: string
  sender_name: string
}

interface ReplyTo {
  message_id: string
  message: string
  sender_name: string
}

interface MessageData {
  id?: string
  message_id?: string
  message: string
  sender_firebase_uid: string
  sender_role: string
  sender_name?: string
  created_at?: unknown
  reply_to?: ReplyTo | null
  file_url?: string
  file_name?: string
  file_type?: 'image' | 'file'
  message_type?: string
  inquiry_link?: boolean
  package_request_link?: boolean
  deleted?: boolean
}

interface HistoryItem {
  id: string
  category?: string
  status?: string
  last_message?: string
  last_message_from?: string
  updated_at?: unknown
  created_by_user_id?: string
  created_by_role?: string
}

interface UserInfo {
  id?: number
  name?: string
  email?: string
  company_id?: number
  company_name?: string
  company_code?: string
}

interface ExternalOpenPayload {
  category?: ChatCategory['key']
  message?: string
}

// ─── Constants ────────────────────────────────────────────────────────────────

const categories: ChatCategory[] = [
  { key: 'hotel', label: 'Hotel', icon: '🏨' },
  { key: 'tour_package', label: 'Tour Package', icon: '🧳' },
  { key: 'visa', label: 'Visa', icon: '🛂' },
  { key: 'field_sales', label: 'Field Sales', icon: '💼' },
  { key: 'general_support', label: 'General Support', icon: '❓' },
]

const STORAGE_KEY = 'chatWidgetState'

// ─── State ────────────────────────────────────────────────────────────────────

const initialized = ref(false)
const loading = ref(false)
const errorText = ref('')

const isMinimized = ref(false)
const currentView = ref<'category' | 'history' | 'messages'>('category')

const currentCategory = ref<ChatCategory['key'] | null>(null)
const currentChatId = ref<string | null>(null)
const headerTitle = ref('Chat Support')
const headerSubtitle = ref('')

const messageInput = ref('')
const messages = ref<MessageData[]>([])
const historyItems = ref<HistoryItem[]>([])

const loadingMessages = ref(false)
const loadingHistory = ref(false)
const sendingMessage = ref(false)
const uploadingFile = ref(false)

const firebaseUid = ref('')
const userInfo = ref<UserInfo>({})
const adminOnline = ref(false)
const highlightedMessageId = ref<string | null>(null)
const imagePreviewUrl = ref<string | null>(null)

// Slide-up menu for attachment buttons
const showAttachMenu = ref(false)

// Reply
const replyTo = ref<ReplyRef | null>(null)

// Context menu
interface CtxMenu { visible: boolean; x: number; y: number; msg: MessageData | null }
const ctxMenu = ref<CtxMenu>({ visible: false, x: 0, y: 0, msg: null })
let pressTimer: number | null = null

// Refs
const fileInputRef = ref<HTMLInputElement | null>(null)
const messagesContainer = ref<HTMLElement | null>(null)
const textareaRef = ref<HTMLTextAreaElement | null>(null)

// Listeners
let unsubscribeMessages: (() => void) | null = null
let unsubscribePresence: (() => void) | null = null
let unsubscribeChatAlerts: (() => void) | null = null

// ─── Computed ─────────────────────────────────────────────────────────────────

const showBackButton = computed(() => currentView.value !== 'category')
const showHistoryButton = computed(() => currentView.value === 'messages')

// ─── Helpers ─────────────────────────────────────────────────────────────────

function firestoreTimestampToDate(value: unknown): Date {
  if (!value) return new Date()
  if (value instanceof Date) return value
  if (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate: () => Date }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate()
  }
  return new Date(value as string)
}

function formatTime(value: unknown): string {
  const date = firestoreTimestampToDate(value)
  return date.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function toTitle(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())
}

function normalizeCategory(key: string | null | undefined): ChatCategory['key'] {
  if (key === 'hotel_package') return 'tour_package'
  if (key === 'hotel' || key === 'tour_package' || key === 'visa' || key === 'field_sales' || key === 'general_support') {
    return key
  }
  return 'general_support'
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  })
}

function playIncomingSound() {
  try {
    const AudioCtx = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!AudioCtx) return

    const ctx = new AudioCtx()
    const now = ctx.currentTime
    const gain = ctx.createGain()
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.exponentialRampToValueAtTime(0.08, now + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28)
    gain.connect(ctx.destination)

    const osc = ctx.createOscillator()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(920, now)
    osc.frequency.exponentialRampToValueAtTime(720, now + 0.22)
    osc.connect(gain)
    osc.start(now)
    osc.stop(now + 0.28)

    osc.onended = () => {
      void ctx.close()
    }
  } catch {
    // Ignore sound errors silently.
  }
}

function stopListening() {
  if (unsubscribeMessages) { unsubscribeMessages(); unsubscribeMessages = null }
}

function stopPresence() {
  if (unsubscribePresence) { unsubscribePresence(); unsubscribePresence = null }
}

function stopChatAlerts() {
  if (unsubscribeChatAlerts) { unsubscribeChatAlerts(); unsubscribeChatAlerts = null }
}

function subscribeToChatAlerts() {
  if (!firebaseUid.value) return

  stopChatAlerts()
  let initializedSnapshot = false

  const q = query(
    collection(firestoreDb, 'chats'),
    where('created_by_user_id', '==', firebaseUid.value),
    where('created_by_role', '==', 'user'),
    where('status', '==', 'open'),
  )

  unsubscribeChatAlerts = onSnapshot(q, (snapshot) => {
    if (!initializedSnapshot) {
      initializedSnapshot = true
      return
    }

    const incomingAdminChange = snapshot.docChanges().find((change) => {
      if (change.type !== 'modified' && change.type !== 'added') return false
      const data = change.doc.data() as Omit<HistoryItem, 'id'>
      return data.last_message_from === 'admin'
    })

    if (!incomingAdminChange) return

    const chatId = incomingAdminChange.doc.id
    const data = incomingAdminChange.doc.data() as Omit<HistoryItem, 'id'>
    const category = normalizeCategory((data.category as string | undefined) || 'general_support')

    maximize()
    currentCategory.value = category
    headerTitle.value = toTitle(category)
    currentView.value = 'messages'

    if (currentChatId.value !== chatId) {
      listenToMessages(chatId)
    }

    playIncomingSound()
  })
}

// ─── Widget persistence ───────────────────────────────────────────────────────

function restoreWidgetState() {
  isMinimized.value = localStorage.getItem(STORAGE_KEY) === 'minimized'
}

function persistWidgetState() {
  localStorage.setItem(STORAGE_KEY, isMinimized.value ? 'minimized' : 'maximized')
}

function updateResponsiveState() {
  // Keep current state while resizing so mobile users can also minimize the widget.
}

function minimize() {
  isMinimized.value = true
  persistWidgetState()
}

function maximize() {
  isMinimized.value = false
  persistWidgetState()
}

// ─── Firebase init ────────────────────────────────────────────────────────────

async function initializeChat() {
  loading.value = true
  errorText.value = ''
  try {
    const payload = await chatService.getFirebaseAuth()
    if (!payload?.success || !payload.firebase_uid) throw new Error('Failed to initialize chat session')
    firebaseUid.value = payload.firebase_uid
    userInfo.value = payload.user || {}
    if (payload.firebase_token) await signInWithCustomToken(firebaseAuth, payload.firebase_token)
    initialized.value = true
    subscribeToAdminPresence()
    subscribeToChatAlerts()
  } catch (error) {
    errorText.value = error instanceof Error ? error.message : 'Failed to initialize chat'
  } finally {
    loading.value = false
  }
}

// ─── Admin presence ───────────────────────────────────────────────────────────

function subscribeToAdminPresence() {
  const q = query(collection(firestoreDb, 'presence'), where('role', '==', 'admin'), where('online', '==', true), limit(1))
  unsubscribePresence = onSnapshot(q, (snap) => { adminOnline.value = !snap.empty })
}

// ─── Category / chat opening ──────────────────────────────────────────────────

async function selectCategory(category: ChatCategory['key']) {
  if (!initialized.value) return
  currentCategory.value = category
  currentView.value = 'messages'
  headerTitle.value = toTitle(category)
  headerSubtitle.value = 'Loading...'
  loadingMessages.value = true
  stopListening()

  try {
    const categoryFilter = category === 'tour_package'
      ? where('category', 'in', ['tour_package', 'hotel_package'])
      : where('category', '==', category)

    const q = query(
      collection(firestoreDb, 'chats'),
      categoryFilter,
      where('status', '==', 'open'),
      where('created_by_user_id', '==', firebaseUid.value),
      where('created_by_role', '==', 'user'),
      limit(1),
    )
    const snapshot = await getDocs(q)
    let chatId: string
    if (!snapshot.empty) {
      chatId = snapshot.docs[0]!.id
    } else {
      chatId = doc(collection(firestoreDb, 'chats')).id
      await setDoc(doc(firestoreDb, 'chats', chatId), {
        chat_id: chatId, category,
        created_by_user_id: firebaseUid.value, created_by_role: 'user',
        sender_name: userInfo.value.name || '',
        sender_company_code: userInfo.value.company_code || '',
        sender_company_name: userInfo.value.company_name || '',
        participants: [firebaseUid.value],
        last_message: '', updated_at: serverTimestamp(), created_at: serverTimestamp(), status: 'open',
      })
    }
    listenToMessages(chatId)
  } catch (error) {
    errorText.value = error instanceof Error ? error.message : 'Failed to open chat category'
    loadingMessages.value = false
  }
}

// ─── Real-time message listener ───────────────────────────────────────────────

function listenToMessages(chatId: string) {
  currentChatId.value = chatId
  currentView.value = 'messages'
  stopListening()
  messages.value = []
  let initializedSnapshot = false

  const q = query(collection(firestoreDb, 'chats', chatId, 'messages'), orderBy('created_at', 'asc'), limit(200))
  unsubscribeMessages = onSnapshot(
    q,
    (snapshot) => {
      loadingMessages.value = false
      const incoming: MessageData[] = []
      snapshot.forEach((d) => incoming.push({ id: d.id, ...(d.data() as Omit<MessageData, 'id'>) }))
      messages.value = incoming

      if (initializedSnapshot) {
        const hasIncomingFromSupport = snapshot.docChanges().some((change) => {
          if (change.type !== 'added') return false
          const data = change.doc.data() as Omit<MessageData, 'id'>
          const senderUid = data.sender_firebase_uid || ''
          const senderRole = data.sender_role || ''
          return senderUid !== firebaseUid.value && senderRole !== 'user'
        })

        if (hasIncomingFromSupport) {
          maximize()
          currentView.value = 'messages'
          playIncomingSound()
        }
      }

      initializedSnapshot = true
      scrollToBottom()
    },
    (error) => { loadingMessages.value = false; errorText.value = 'Failed to load messages: ' + error.message },
  )
}

function openChat(chatId: string) {
  listenToMessages(chatId)
  currentView.value = 'messages'
}

// ─── Firestore write helper ───────────────────────────────────────────────────

async function dispatchToFirestore(opts: {
  message: string
  messageType?: string
  fileUrl?: string
  fileName?: string
  fileType?: 'image' | 'file'
}) {
  const chatId = currentChatId.value!
  const messageId = doc(collection(firestoreDb, 'chats', chatId, 'messages')).id
  const now = serverTimestamp()

  const data: Record<string, unknown> = {
    message_id: messageId, chat_id: chatId,
    sender_firebase_uid: firebaseUid.value, sender_role: 'user',
    sender_name: userInfo.value.name || '',
    message: opts.message, message_type: opts.messageType ?? 'text',
    created_at: now, deleted: false,
  }
  
  // Add inquiry_link flag if messageType is inquiry_link
  if (opts.messageType === 'inquiry_link') {
    data['inquiry_link'] = true
  }

  if (replyTo.value) {
    data['reply_to'] = { message_id: replyTo.value.id, message: replyTo.value.message, sender_name: replyTo.value.sender_name }
    replyTo.value = null
  }
  if (opts.fileUrl) {
    data['file_url'] = opts.fileUrl
    data['file_name'] = opts.fileName ?? ''
    data['file_type'] = opts.fileType ?? 'file'
  }

  await setDoc(doc(firestoreDb, 'chats', chatId, 'messages', messageId), data)
  const lastMsg = opts.fileUrl ? (opts.fileType === 'image' ? '📷 Image' : `📎 ${opts.fileName || 'File'}`) : opts.message
  await updateDoc(doc(firestoreDb, 'chats', chatId), { last_message: lastMsg, updated_at: now, last_message_from: 'user' })
  sendAdminNotification(chatId, messageId)
}

// ─── Send text message ────────────────────────────────────────────────────────

async function sendMessage() {
  if (!currentChatId.value || sendingMessage.value) return
  const msg = messageInput.value.trim()
  if (!msg) return
  sendingMessage.value = true
  try {
    await dispatchToFirestore({ message: msg })
    messageInput.value = ''
    resetTextareaHeight()
  } catch (error) {
    errorText.value = error instanceof Error ? error.message : 'Failed to send message'
  } finally {
    sendingMessage.value = false
  }
}

// ─── File / image upload ──────────────────────────────────────────────────────

function onFileSelected(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (file) void uploadAndSend(file)
}

async function uploadAndSend(file: File) {
  if (!currentChatId.value) return
  uploadingFile.value = true
  try {
    const formData = new FormData()
    formData.append('file', file)
    const apiBase = (import.meta.env.VITE_API_URL as string) || '/api/v1'
    const token = localStorage.getItem('auth_token') || ''
    const res = await fetch(`${apiBase}/chat/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
      body: formData,
    })
    const json = (await res.json()) as { success: boolean; file_url?: string; file_name?: string; file_type?: 'image' | 'file'; message?: string }
    if (!json.success) throw new Error(json.message || 'Upload failed')
    await dispatchToFirestore({ message: json.file_name || file.name, messageType: json.file_type ?? 'file', fileUrl: json.file_url, fileName: json.file_name ?? file.name, fileType: json.file_type })
  } catch (error) {
    errorText.value = error instanceof Error ? error.message : 'Upload failed'
  } finally {
    uploadingFile.value = false
  }
}

// ─── Admin notification (non-blocking, throttled) ─────────────────────────────

function sendAdminNotification(chatId: string, messageId: string) {
  const key = `lastNotificationSent_${chatId}`
  const last = localStorage.getItem(key)
  const nowMs = Date.now()
  if (last && nowMs - parseInt(last) < 10 * 60 * 1000) return
  const baseUrl = (import.meta.env.VITE_ASSET_URL as string) || ''
  fetch(`${baseUrl}/api/admin/send-notification`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ chat_id: chatId, message_id: messageId }),
  }).then(() => localStorage.setItem(key, nowMs.toString())).catch(() => {})
}

// ─── Context menu ─────────────────────────────────────────────────────────────

function showCtxMenu(clientX: number, clientY: number, msg: MessageData) {
  const mW = 145, mH = 170
  ctxMenu.value = {
    visible: true,
    x: Math.min(clientX, window.innerWidth - mW - 6),
    y: Math.min(clientY, window.innerHeight - mH - 6),
    msg,
  }
}

function hideCtxMenu() { ctxMenu.value.visible = false }

function onMsgContextMenu(e: MouseEvent, msg: MessageData) {
  e.preventDefault(); e.stopPropagation()
  showCtxMenu(e.clientX, e.clientY, msg)
}

function onMsgTouchStart(e: TouchEvent, msg: MessageData) {
  if (pressTimer !== null) { clearTimeout(pressTimer); pressTimer = null }
  pressTimer = window.setTimeout(() => {
    pressTimer = null
    const t = e.touches[0]
    if (t) showCtxMenu(t.clientX, t.clientY, msg)
  }, 500)
}

function onMsgTouchEnd() {
  if (pressTimer !== null) { clearTimeout(pressTimer); pressTimer = null }
}

function onInputKeyDown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    void sendMessage()
  }
}

function scrollToMessage(messageId: string | null | undefined) {
  if (!messageId) return
  const target = document.getElementById(`msg-${messageId}`)
  if (!target) return
  target.scrollIntoView({ behavior: 'smooth', block: 'center' })
  highlightedMessageId.value = messageId
  window.setTimeout(() => {
    if (highlightedMessageId.value === messageId) highlightedMessageId.value = null
  }, 1400)
}

function openImagePreview(url: string | undefined) {
  if (!url) return
  imagePreviewUrl.value = url
}

function closeImagePreview() {
  imagePreviewUrl.value = null
}

async function downloadFile(url: string | undefined, fileName: string | undefined) {
  if (!url) return

  const safeName = (fileName || 'download').trim() || 'download'
  try {
    const response = await fetch(url)
    if (!response.ok) throw new Error('download failed')

    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = safeName
    link.rel = 'noopener noreferrer'
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(blobUrl)
  } catch {
    const link = document.createElement('a')
    link.href = url
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    link.download = safeName
    document.body.appendChild(link)
    link.click()
    link.remove()
  }
}

function ctxCopy(msg: MessageData | null) {
  if (!msg || msg.deleted) return
  navigator.clipboard.writeText(msg.message || '').catch(() => {})
  hideCtxMenu()
}

function ctxReply(msg: MessageData | null) {
  if (!msg || msg.deleted) return
  replyTo.value = {
    id: msg.id || msg.message_id || '',
    message: msg.message,
    sender_name: msg.sender_name || (msg.sender_firebase_uid === firebaseUid.value ? 'You' : 'Support'),
  }
  hideCtxMenu()
  nextTick(() => document.getElementById('chatMessageInput')?.focus())
}

async function ctxDelete(msg: MessageData | null) {
  if (!msg || !currentChatId.value) return
  const msgId = msg.id || msg.message_id
  if (!msgId) return
  try {
    await updateDoc(doc(firestoreDb, 'chats', currentChatId.value, 'messages', msgId), { deleted: true })
  } catch { errorText.value = 'Failed to delete message' }
  hideCtxMenu()
}

async function ctxEdit(msg: MessageData | null) {
  if (!msg || !currentChatId.value || msg.deleted) return
  const msgId = msg.id || msg.message_id
  if (!msgId) return
  const nextText = window.prompt('Edit message', msg.message || '')
  if (nextText === null) {
    hideCtxMenu()
    return
  }
  const trimmed = nextText.trim()
  if (!trimmed) {
    hideCtxMenu()
    return
  }
  try {
    await updateDoc(doc(firestoreDb, 'chats', currentChatId.value, 'messages', msgId), {
      message: trimmed,
      edited_at: serverTimestamp(),
    })
  } catch {
    errorText.value = 'Failed to edit message'
  }
  hideCtxMenu()
}

// ─── Chat history ─────────────────────────────────────────────────────────────

async function openHistory() {
  if (!currentCategory.value) return
  currentView.value = 'history'
  loadingHistory.value = true
  try {
    const categoryFilter = currentCategory.value === 'tour_package'
      ? where('category', 'in', ['tour_package', 'hotel_package'])
      : where('category', '==', currentCategory.value)
    const q = query(collection(firestoreDb, 'chats'), categoryFilter, limit(100))
    const snapshot = await getDocs(q)
    const userChats: HistoryItem[] = []
    snapshot.forEach((d) => {
      const data = d.data()
      if (data['created_by_user_id'] === firebaseUid.value && data['created_by_role'] === 'user')
        userChats.push({ id: d.id, ...(data as Omit<HistoryItem, 'id'>) })
    })
    userChats.sort((a, b) => firestoreTimestampToDate(b.updated_at).getTime() - firestoreTimestampToDate(a.updated_at).getTime())
    historyItems.value = userChats
  } catch (error) {
    errorText.value = error instanceof Error ? error.message : 'Failed to load history'
  } finally {
    loadingHistory.value = false
  }
}

// ─── Navigation ───────────────────────────────────────────────────────────────

function backToCategories() {
  currentView.value = 'category'; currentChatId.value = null
  headerTitle.value = 'Chat Support'; headerSubtitle.value = ''
  replyTo.value = null; stopListening()
}

function closeHistoryToMessages() { currentView.value = 'messages' }

async function openFromExternal(payload: ExternalOpenPayload) {
  if (!initialized.value) {
    await initializeChat()
  }

  maximize()

  const category = normalizeCategory(payload.category || 'general_support')
  await selectCategory(category)

  if (payload.message && payload.message.trim()) {
    messageInput.value = payload.message
    await sendMessage()
  }
}

function onExternalOpen(event: Event) {
  const customEvent = event as CustomEvent<ExternalOpenPayload>
  void openFromExternal(customEvent.detail || {})
}

// ─── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(async () => {
  restoreWidgetState(); updateResponsiveState()
  window.addEventListener('resize', updateResponsiveState)
  window.addEventListener('chat-widget:open', onExternalOpen as EventListener)
  window.addEventListener('keydown', onEscKey)
  document.addEventListener('click', hideCtxMenu)
  document.addEventListener('click', closeInquiryPickers)
  await initializeChat()

  // Check URL hash for direct inquiry popup open
  if (window.location.hash === '#hotel-inquiry') {
    // Auto-select hotel category and open inquiry popup
    setTimeout(() => {
      void selectCategory('hotel')
      setTimeout(() => {
        openInquiryPopup()
        // Clear the hash
        history.replaceState(null, '', window.location.pathname + window.location.search)
      }, 300)
    }, 500)
  }

  // Check URL hash for direct package request popup open
  if (window.location.hash === '#package-request') {
    // Auto-select tour_package category and open package request popup
    setTimeout(() => {
      void selectCategory('tour_package')
      setTimeout(() => {
        openPackagePopup()
        // Clear the hash
        history.replaceState(null, '', window.location.pathname + window.location.search)
      }, 300)
    }, 500)
  }
})

onUnmounted(() => {
  stopListening(); stopPresence(); stopChatAlerts()
  window.removeEventListener('resize', updateResponsiveState)
  window.removeEventListener('chat-widget:open', onExternalOpen as EventListener)
  window.removeEventListener('keydown', onEscKey)
  document.removeEventListener('click', hideCtxMenu)
  document.removeEventListener('click', closeInquiryPickers)
})

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    closeImagePreview()
  }
}

function autoResize() {
  const el = textareaRef.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = Math.min(el.scrollHeight, 120) + 'px'
}

function resetTextareaHeight() {
  const el = textareaRef.value
  if (!el) return
  el.style.height = ''
}

// ─── Hotel Inquiry Popup ──────────────────────────────────────────────────────

const showInquiryPopup = ref(false)
const inquiryFormError = ref('')
const inquiryTodayDate = new Date().toISOString().split('T')[0] ?? ''

const inquiryFilters = ref({
  hotel_name_area_city: '',
  check_in_date: '',
  check_out_date: '',
  guest_adt: '1',
  guest_child: '0',
  child_age: '',
  room_qty: '1',
  meal_plan: '' as '' | 'room_only' | 'breakfast_included',
  budget_type: '' as '' | 'per_night' | 'total' | 'no_budget',
  budget_amount: '',
  remarks: ''
})

const inquiryNightCount = computed(() => {
  if (!inquiryFilters.value.check_in_date || !inquiryFilters.value.check_out_date) return 0
  const d1 = new Date(inquiryFilters.value.check_in_date + 'T00:00:00')
  const d2 = new Date(inquiryFilters.value.check_out_date + 'T00:00:00')
  return Math.max(0, Math.round((d2.getTime() - d1.getTime()) / 86400000))
})

const inquiryGuestLabel = computed(() => {
  const rooms = Number(inquiryFilters.value.room_qty) || 1
  const adults = Number(inquiryFilters.value.guest_adt) || 1
  const children = Number(inquiryFilters.value.guest_child) || 0
  return `${rooms} room${rooms !== 1 ? 's' : ''}, ${adults} adult${adults !== 1 ? 's' : ''}, ${children} child${children !== 1 ? 'ren' : ''}`
})

// ── Booking.com-style picker state ──────────────────────────────────
const showInquiryDatePicker = ref(false)
const showInquiryGuestPicker = ref(false)
const inquiryCalViewYear = ref(new Date().getFullYear())
const inquiryCalViewMonth = ref(new Date().getMonth())
const inquiryCalPickingStart = ref(true)
const inquiryCalHoverDay = ref('')
const inquiryDateBarRef = ref<HTMLElement>()
const inquiryGuestBarRef = ref<HTMLElement>()
const inquiryCalPopupStyle = ref<Record<string, string>>({})
const inquiryGuestPopupStyle = ref<Record<string, string>>({})

function calcPopupPos(el: HTMLElement, minW: number): Record<string, string> {
  const r = el.getBoundingClientRect()
  const left = Math.max(4, Math.min(r.left, window.innerWidth - minW - 4))
  return { position: 'fixed', top: `${r.bottom + 4}px`, left: `${left}px`, minWidth: `${Math.max(minW, r.width)}px`, zIndex: '100003' }
}

function fmtDateDisplay(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function fmtMonthDay(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso + 'T00:00:00')
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long' })
}

function calIso(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}

interface CalMonth { year: number; month: number; name: string; cells: (number | null)[] }

const inquiryCalMonthsData = computed((): [CalMonth, CalMonth] => {
  const result: CalMonth[] = []
  for (let offset = 0; offset < 2; offset++) {
    let m = inquiryCalViewMonth.value + offset
    let y = inquiryCalViewYear.value
    if (m > 11) { m -= 12; y += 1 }
    const daysInMonth = new Date(y, m + 1, 0).getDate()
    const startOffset = (new Date(y, m, 1).getDay() + 6) % 7 // Mon=0
    const cells: (number | null)[] = Array(startOffset).fill(null)
    for (let d = 1; d <= daysInMonth; d++) cells.push(d)
    result.push({
      year: y, month: m,
      name: new Date(y, m, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      cells,
    })
  }
  return result as [CalMonth, CalMonth]
})

function inquiryCalDayClasses(y: number, m: number, d: number): string[] {
  const iso = calIso(y, m, d)
  const ci = inquiryFilters.value.check_in_date
  const co = inquiryFilters.value.check_out_date
  if (iso < inquiryTodayDate) return ['bk-cal-day', 'past']
  if (ci && co && iso === ci && iso === co) return ['bk-cal-day', 'start', 'end']
  if (iso === ci) return ['bk-cal-day', 'start']
  if (iso === co) return ['bk-cal-day', 'end']
  if (ci && co && iso > ci && iso < co) return ['bk-cal-day', 'range']
  if (!inquiryCalPickingStart.value && ci && !co && inquiryCalHoverDay.value > ci) {
    if (iso === inquiryCalHoverDay.value) return ['bk-cal-day', 'hover-end']
    if (iso > ci && iso < inquiryCalHoverDay.value) return ['bk-cal-day', 'hover-range']
  }
  return ['bk-cal-day']
}

function inquiryCalClick(y: number, m: number, d: number) {
  const iso = calIso(y, m, d)
  if (iso < inquiryTodayDate) return
  if (inquiryCalPickingStart.value) {
    inquiryFilters.value.check_in_date = iso
    inquiryFilters.value.check_out_date = ''
    inquiryCalPickingStart.value = false
    inquiryCalHoverDay.value = ''
  } else {
    if (iso <= inquiryFilters.value.check_in_date) {
      inquiryFilters.value.check_in_date = iso
      inquiryFilters.value.check_out_date = ''
    } else {
      inquiryFilters.value.check_out_date = iso
      inquiryCalPickingStart.value = true
      inquiryCalHoverDay.value = ''
      showInquiryDatePicker.value = false
    }
  }
}

function inquiryCalPrev() {
  if (inquiryCalViewMonth.value === 0) { inquiryCalViewMonth.value = 11; inquiryCalViewYear.value-- }
  else inquiryCalViewMonth.value--
}

function inquiryCalNext() {
  if (inquiryCalViewMonth.value === 11) { inquiryCalViewMonth.value = 0; inquiryCalViewYear.value++ }
  else inquiryCalViewMonth.value++
}

function openInquiryDatePicker(e: Event) {
  e.stopPropagation()
  showInquiryGuestPicker.value = false
  showInquiryDatePicker.value = !showInquiryDatePicker.value
  if (showInquiryDatePicker.value) {
    inquiryCalPickingStart.value = !inquiryFilters.value.check_in_date || !!inquiryFilters.value.check_out_date
    if (inquiryDateBarRef.value) inquiryCalPopupStyle.value = calcPopupPos(inquiryDateBarRef.value, 660)
  }
}

function openInquiryGuestPicker(e: Event) {
  e.stopPropagation()
  showInquiryDatePicker.value = false
  showInquiryGuestPicker.value = !showInquiryGuestPicker.value
  if (showInquiryGuestPicker.value && inquiryGuestBarRef.value) {
    inquiryGuestPopupStyle.value = calcPopupPos(inquiryGuestBarRef.value, 290)
  }
}

function closeInquiryPickers() {
  showInquiryDatePicker.value = false
  showInquiryGuestPicker.value = false
}

function incrInquiryGuest(field: 'guest_adt' | 'guest_child' | 'room_qty', delta: number, min: number) {
  const cur = Number(inquiryFilters.value[field]) || min
  inquiryFilters.value[field] = String(Math.max(min, cur + delta))
}

function openInquiryPopup() {
  showInquiryPopup.value = true
  // reset calendar view to current month when opening
  inquiryCalViewYear.value = new Date().getFullYear()
  inquiryCalViewMonth.value = new Date().getMonth()
}

function closeInquiryPopup() {
  showInquiryPopup.value = false
  inquiryFormError.value = ''
  showInquiryDatePicker.value = false
  showInquiryGuestPicker.value = false
}

function resetInquiryFilters() {
  inquiryFilters.value = {
    hotel_name_area_city: '',
    check_in_date: '',
    check_out_date: '',
    guest_adt: '1',
    guest_child: '0',
    child_age: '',
    room_qty: '1',
    meal_plan: '',
    budget_type: '',
    budget_amount: '',
    remarks: ''
  }
  inquiryFormError.value = ''
}

function validateInquiryForm() {
  const f = inquiryFilters.value
  if (!f.hotel_name_area_city.trim()) return 'Hotel Name / Area / City is required.'
  if (!f.check_in_date) return 'Check-in Date is required.'
  if (!f.check_out_date) return 'Check-out Date is required.'
  if (!f.guest_adt || Number(f.guest_adt) <= 0) return 'ADT is required.'
  if (!f.room_qty || Number(f.room_qty) <= 0) return 'Room Qty is required.'
  if (!f.meal_plan) return 'Meal Plan is required.'
  if (f.check_in_date < inquiryTodayDate) return 'Check-in date cannot be in the past.'
  if (f.check_out_date <= f.check_in_date) return 'Check-out date must be after check-in date.'
  return ''
}

function buildInquiryMessage() {
  const f = inquiryFilters.value
  const mealPlan = f.meal_plan === 'room_only' ? 'Room Only' : f.meal_plan === 'breakfast_included' ? 'Breakfast included' : '-'
  const budgetLabel = f.budget_type === 'per_night' ? 'Budget Per Night (BDT)' : f.budget_type === 'total' ? 'Budget Total (BDT)' : 'No Budget Specified'
  return [
    '🔰 Hotel Inquiry 🔰',
    '=================',
    `Hotel Name / Area / City: ${f.hotel_name_area_city || '-'}`,
    `Check-in Date: ${f.check_in_date || '-'}`,
    `Check-out Date: ${f.check_out_date || '-'}`,
    '',
    `Guest qnt: ADT: ${f.guest_adt || '-'} | Child: ${f.guest_child || '-'} (Age: ${f.child_age || '-'})`,
    `Room Qty: ${f.room_qty || '-'}`,
    `Meal Plan: ${mealPlan}`,
    '',
    `${budgetLabel}: ${f.budget_amount || '-'}`,
    '',
    'Remarks:',
    f.remarks || ''
  ].join('\n')
}

async function submitInquiryForm() {
  inquiryFormError.value = ''
  const error = validateInquiryForm()
  if (error) { inquiryFormError.value = error; return }
  if (!currentChatId.value) return
  const message = buildInquiryMessage()
  closeInquiryPopup()
  resetInquiryFilters()
  sendingMessage.value = true
  try {
    await dispatchToFirestore({ message })
  } catch (err) {
    errorText.value = err instanceof Error ? err.message : 'Failed to send inquiry'
  } finally {
    sendingMessage.value = false
  }
}

function setInquiryBudgetType(type: '' | 'per_night' | 'total' | 'no_budget') {
  inquiryFilters.value.budget_type = type
  inquiryFilters.value.budget_amount = ''
}

// ─── Package Request Popup ───────────────────────────────────────────────────

const showPackagePopup = ref(false)
const packageFormError = ref('')
const packageCountries = ref<PackageCountry[]>([])
const packageCountriesLoaded = ref(false)

const pkgYears = Array.from({ length: 3 }, (_, i) => new Date().getFullYear() + i)
const pkgMonths = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]
const pkgWeeks = [1, 2, 3, 4]

const packageFilters = ref({
  country_id: null as number | null,
  travel_year: new Date().getFullYear(),
  travel_month: new Date().getMonth() + 1,
  travel_week: 1,
  nights: 1,
  adult_qty: 1,
  child_qty: 0,
  child_age: '',
  infant_qty: 0,
  room_qty: 1,
  special_requirements: ''
})

function getWeekOrdinal(week: number): string {
  if (week === 1) return '1st'
  if (week === 2) return '2nd'
  if (week === 3) return '3rd'
  return week + 'th'
}

function getNightsDaysText(nights: number): string {
  return `${nights} Night ${nights + 1} Days`
}

function countryName(id: number | null): string {
  if (!id) return '-'
  return packageCountries.value.find((c) => c.id === id)?.name || '-'
}

async function loadPackageCountries() {
  if (packageCountriesLoaded.value) return
  try {
    const response = await packageRequestService.getCountries()
    packageCountries.value = response.data || []
    packageCountriesLoaded.value = true
  } catch (error) {
    console.error('Failed to load countries:', error)
    packageCountriesLoaded.value = true
  }
}

function openPackagePopup() {
  showPackagePopup.value = true
  void loadPackageCountries()
}

function closePackagePopup() {
  showPackagePopup.value = false
  packageFormError.value = ''
}

function resetPackageFilters() {
  packageFilters.value = {
    country_id: null,
    travel_year: new Date().getFullYear(),
    travel_month: new Date().getMonth() + 1,
    travel_week: 1,
    nights: 1,
    adult_qty: 1,
    child_qty: 0,
    child_age: '',
    infant_qty: 0,
    room_qty: 1,
    special_requirements: ''
  }
  packageFormError.value = ''
}

function validatePackageForm() {
  const f = packageFilters.value
  if (!f.country_id) return 'Destination Country is required.'
  if (!f.nights || Number(f.nights) <= 0) return 'Nights is required.'
  if (!f.adult_qty || Number(f.adult_qty) <= 0) return 'Adult Passengers is required.'
  if (!f.room_qty || Number(f.room_qty) <= 0) return 'Room Quantity is required.'
  return ''
}

function buildPackageMessage() {
  const f = packageFilters.value
  return [
    '🧳 Package Request 🧳',
    '=================',
    `Destination Country: ${countryName(f.country_id)}`,
    `Travel: ${pkgMonths[f.travel_month - 1]} ${f.travel_year}, ${getWeekOrdinal(f.travel_week)} Week`,
    `Nights: ${f.nights || '-'} (${getNightsDaysText(f.nights || 0)})`,
    '',
    `Guests: ADT: ${f.adult_qty || '-'} | Child: ${f.child_qty || '-'} (Age: ${f.child_age || '-'}) | Infant: ${f.infant_qty || '-'}`,
    `Room Qty: ${f.room_qty || '-'}`,
    '',
    'Special Requirements:',
    f.special_requirements || '-'
  ].join('\n')
}

async function submitPackageForm() {
  packageFormError.value = ''
  const error = validatePackageForm()
  if (error) { packageFormError.value = error; return }
  if (!currentChatId.value) return
  const message = buildPackageMessage()
  closePackagePopup()
  resetPackageFilters()
  sendingMessage.value = true
  try {
    await dispatchToFirestore({ message })
  } catch (err) {
    errorText.value = err instanceof Error ? err.message : 'Failed to send package request'
  } finally {
    sendingMessage.value = false
  }
}

function incrPackageGuest(field: 'adult_qty' | 'child_qty' | 'infant_qty' | 'room_qty', delta: number, min: number) {
  const cur = Number(packageFilters.value[field]) || min
  packageFilters.value[field] = Math.max(min, cur + delta)
}
</script>

<template>
  <div>
    <Teleport to="body">
      <div v-if="imagePreviewUrl" class="chat-image-modal" @click="closeImagePreview">
        <button type="button" class="chat-image-modal-close" @click.stop="closeImagePreview">x</button>
        <img :src="imagePreviewUrl" class="chat-image-modal-img" alt="Preview image" @click.stop />
      </div>
    </Teleport>

    <!-- Context menu teleported to body to avoid overflow clipping -->
    <Teleport to="body">
      <div
        v-if="ctxMenu.visible"
        class="chat-ctx-menu"
        :style="{ top: ctxMenu.y + 'px', left: ctxMenu.x + 'px' }"
        @click.stop
      >
        <button class="ctx-item" @click="ctxCopy(ctxMenu.msg)"><i class="fas fa-copy"></i> Copy</button>
        <button class="ctx-item" @click="ctxReply(ctxMenu.msg)"><i class="fas fa-reply"></i> Reply</button>
        <button
          v-if="ctxMenu.msg && ctxMenu.msg.sender_firebase_uid === firebaseUid && !ctxMenu.msg.deleted"
          class="ctx-item"
          @click="ctxEdit(ctxMenu.msg)"
        ><i class="fas fa-pen"></i> Edit</button>
        <button
          v-if="ctxMenu.msg && ctxMenu.msg.sender_firebase_uid === firebaseUid && !ctxMenu.msg.deleted"
          class="ctx-item ctx-delete"
          @click="ctxDelete(ctxMenu.msg)"
        ><i class="fas fa-trash"></i> Delete</button>
      </div>
    </Teleport>

    <!-- Hotel Inquiry Popup -->
    <Teleport to="body">
      <div v-if="showInquiryPopup" class="chat-inquiry-overlay" @click.self="closeInquiryPopup">
        <div class="chat-inquiry-popup">
          <div class="chat-inquiry-header">
            <span><i class="fas fa-hotel"></i> Hotel Inquiry</span>
            <button type="button" class="chat-inquiry-close" @click="closeInquiryPopup">&times;</button>
          </div>
          <form class="chat-inquiry-body" @submit.prevent="submitInquiryForm">
            <div class="inq-field">
              <label>Hotel Name / Area / City <span class="inq-req">*</span></label>
              <input v-model="inquiryFilters.hotel_name_area_city" type="text" class="inq-input" placeholder="Hotel name, area or city" />
            </div>

            <!-- Date Picker Bar (Booking.com style) -->
            <div class="inq-field">
              <label>Dates <span class="inq-req">*</span></label>
              <div class="bk-date-bar" ref="inquiryDateBarRef" @click="openInquiryDatePicker($event)">
                <div class="bk-date-section">
                  <span v-if="inquiryFilters.check_in_date" class="bk-date-value">{{ fmtDateDisplay(inquiryFilters.check_in_date) }}</span>
                  <span v-else class="bk-date-placeholder">Check-in</span>
                </div>
                <div class="bk-nights-section">
                  {{ inquiryNightCount > 0 ? inquiryNightCount + (inquiryNightCount === 1 ? ' night' : ' nights') : '&mdash;' }}
                </div>
                <div class="bk-date-section">
                  <span v-if="inquiryFilters.check_out_date" class="bk-date-value">{{ fmtDateDisplay(inquiryFilters.check_out_date) }}</span>
                  <span v-else class="bk-date-placeholder">Check-out</span>
                </div>
              </div>
            </div>

            <!-- Rooms & Guests Bar -->
            <div class="inq-field">
              <label>Rooms and guests</label>
              <div class="bk-guest-bar" ref="inquiryGuestBarRef" @click="openInquiryGuestPicker($event)">
                <i class="fa fa-user bk-guest-icon"></i>
                <span class="bk-guest-summary">{{ inquiryGuestLabel }}</span>
                <i :class="['fa', showInquiryGuestPicker ? 'fa-chevron-up' : 'fa-chevron-down']" class="bk-guest-caret"></i>
              </div>

              <!-- Guest picker dropdown -->
              <div v-if="showInquiryGuestPicker" class="bk-guest-popup" :style="inquiryGuestPopupStyle" @click.stop>
                <!-- Rooms -->
                <div class="bk-guest-row">
                  <div class="bk-guest-label-wrap">
                    <div class="bk-guest-name">Rooms</div>
                  </div>
                  <div class="bk-guest-ctrl">
                    <button type="button" class="bk-guest-btn" :disabled="(Number(inquiryFilters.room_qty) || 1) <= 1" @click="incrInquiryGuest('room_qty', -1, 1)">&minus;</button>
                    <span class="bk-guest-num">{{ inquiryFilters.room_qty || 1 }}</span>
                    <button type="button" class="bk-guest-btn" @click="incrInquiryGuest('room_qty', 1, 1)">+</button>
                  </div>
                </div>
                <!-- Adults -->
                <div class="bk-guest-row">
                  <div class="bk-guest-label-wrap">
                    <div class="bk-guest-name">Adults</div>
                    <div class="bk-guest-hint">18+ yrs</div>
                  </div>
                  <div class="bk-guest-ctrl">
                    <button type="button" class="bk-guest-btn" :disabled="(Number(inquiryFilters.guest_adt) || 1) <= 1" @click="incrInquiryGuest('guest_adt', -1, 1)">&minus;</button>
                    <span class="bk-guest-num">{{ inquiryFilters.guest_adt || 1 }}</span>
                    <button type="button" class="bk-guest-btn" @click="incrInquiryGuest('guest_adt', 1, 1)">+</button>
                  </div>
                </div>
                <!-- Children -->
                <div class="bk-guest-row">
                  <div class="bk-guest-label-wrap">
                    <div class="bk-guest-name">Children</div>
                    <div class="bk-guest-hint">0&ndash;17 yrs</div>
                  </div>
                  <div class="bk-guest-ctrl">
                    <button type="button" class="bk-guest-btn" :disabled="(Number(inquiryFilters.guest_child) || 0) <= 0" @click="incrInquiryGuest('guest_child', -1, 0)">&minus;</button>
                    <span class="bk-guest-num">{{ inquiryFilters.guest_child || 0 }}</span>
                    <button type="button" class="bk-guest-btn" @click="incrInquiryGuest('guest_child', 1, 0)">+</button>
                  </div>
                </div>
                <!-- Child ages -->
                <div v-if="Number(inquiryFilters.guest_child) > 0" class="mt-2 mb-1">
                  <label class="form-label" style="font-size: 0.78rem; margin-bottom: 2px;">Child Ages (comma-separated)</label>
                  <input v-model="inquiryFilters.child_age" type="text" class="form-control form-control-sm" placeholder="e.g. 4, 8" />
                </div>
                <button type="button" class="btn btn-primary bk-done-btn" @click="showInquiryGuestPicker = false">Done</button>
              </div>
            </div>

            <div class="inq-field">
              <label>Meal Plan <span class="inq-req">*</span></label>
              <select v-model="inquiryFilters.meal_plan" class="inq-input">
                <option value="">Select Meal Plan</option>
                <option value="room_only">Room Only</option>
                <option value="breakfast_included">Breakfast included</option>
              </select>
            </div>
            <div class="inq-field">
              <label>Budget Option</label>
              <div class="inq-budget-opts">
                <label class="inq-radio">
                  <input type="radio" name="inq_budget_type" :checked="inquiryFilters.budget_type === 'per_night'" @change="setInquiryBudgetType('per_night')" />
                  Per night
                </label>
                <label class="inq-radio">
                  <input type="radio" name="inq_budget_type" :checked="inquiryFilters.budget_type === 'total'" @change="setInquiryBudgetType('total')" />
                  Total
                </label>
                <label class="inq-radio">
                  <input type="radio" name="inq_budget_type" :checked="inquiryFilters.budget_type === 'no_budget'" @change="setInquiryBudgetType('no_budget')" />
                  No Budget
                </label>
              </div>
            </div>
            <div v-if="inquiryFilters.budget_type && inquiryFilters.budget_type !== 'no_budget'" class="inq-field">
              <label>{{ inquiryFilters.budget_type === 'per_night' ? 'Budget/Night (BDT)' : 'Budget Total (BDT)' }}</label>
              <input v-model="inquiryFilters.budget_amount" type="number" min="1" class="inq-input" placeholder="0" />
            </div>
            <div class="inq-field">
              <label>Remarks</label>
              <textarea v-model="inquiryFilters.remarks" class="inq-input" rows="2" placeholder="If any specific hotel mandatory, mention clearly. Flexible options দিলে better rate share করা যাবে"></textarea>
            </div>
            <div v-if="inquiryFormError" class="inq-error">{{ inquiryFormError }}</div>
            <div class="inq-footer">
              <button type="button" class="inq-btn-cancel" @click="closeInquiryPopup">Cancel</button>
              <button type="submit" class="inq-btn-submit" :disabled="sendingMessage">
                <i class="fas fa-paper-plane"></i> Send Inquiry
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- Hotel Calendar Picker (Teleport to body to avoid clipping) -->
    <Teleport to="body">
      <div v-if="showInquiryDatePicker" class="bk-cal-popup" :style="inquiryCalPopupStyle" @click.stop>
        <div class="bk-cal-nav">
          <button type="button" class="bk-cal-nav-btn" @click.stop="inquiryCalPrev">&#8249;</button>
          <div class="bk-cal-month-names">
            <strong v-for="mth in inquiryCalMonthsData" :key="mth.name">{{ mth.name }}</strong>
          </div>
          <button type="button" class="bk-cal-nav-btn" @click.stop="inquiryCalNext">&#8250;</button>
        </div>
        <div class="bk-cal-grid">
          <div v-for="mth in inquiryCalMonthsData" :key="mth.name" class="bk-cal-month">
            <div class="bk-cal-dow">
              <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span class="bk-weekend">Sat</span><span class="bk-weekend">Sun</span>
            </div>
            <div class="bk-cal-days">
              <template v-for="(day, i) in mth.cells" :key="i">
                <div v-if="day === null" class="bk-cal-day empty"></div>
                <div v-else
                  :class="inquiryCalDayClasses(mth.year, mth.month, day)"
                  @click.stop="inquiryCalClick(mth.year, mth.month, day)"
                  @mouseenter="inquiryCalHoverDay = calIso(mth.year, mth.month, day)"
                  @mouseleave="inquiryCalHoverDay = ''"
                >
                  <span class="bk-day-num">{{ day }}</span>
                  <span
                    v-if="!inquiryCalPickingStart && inquiryFilters.check_in_date && !inquiryFilters.check_out_date && inquiryCalHoverDay === calIso(mth.year, mth.month, day) && calIso(mth.year, mth.month, day) > inquiryFilters.check_in_date"
                    class="bk-cal-tooltip"
                  >Check-out on {{ fmtMonthDay(inquiryCalHoverDay) }}</span>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div class="bk-cal-bottom">
          <span class="bk-cal-holiday-note">&#8226; Hover to show holidays</span>
          <div v-if="inquiryFilters.check_in_date && inquiryFilters.check_out_date" class="bk-cal-range-text">
            {{ fmtDateDisplay(inquiryFilters.check_in_date) }} &ndash; {{ fmtDateDisplay(inquiryFilters.check_out_date) }}
            <strong>({{ inquiryNightCount }} night{{ inquiryNightCount !== 1 ? 's' : '' }})</strong>
            <div class="bk-cal-tz-note">All dates are in local time</div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Package Request Popup -->
    <Teleport to="body">
      <div v-if="showPackagePopup" class="chat-inquiry-overlay" @click.self="closePackagePopup">
        <div class="chat-inquiry-popup">
          <div class="chat-inquiry-header" style="background: linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)">
            <span><i class="fas fa-suitcase"></i> Package Request</span>
            <button type="button" class="chat-inquiry-close" @click="closePackagePopup">&times;</button>
          </div>
          <form class="chat-inquiry-body" @submit.prevent="submitPackageForm">
            <div class="inq-field">
              <label>Select Destination Country <span class="inq-req">*</span></label>
              <select v-model="packageFilters.country_id" class="inq-input">
                <option :value="null">Select Destination Country</option>
                <option v-for="c in packageCountries" :key="c.id" :value="c.id">{{ c.name }}</option>
              </select>
            </div>
            <div class="inq-row">
              <div class="inq-field">
                <label>Travel Year <span class="inq-req">*</span></label>
                <select v-model="packageFilters.travel_year" class="inq-input">
                  <option v-for="y in pkgYears" :key="y" :value="y">{{ y }}</option>
                </select>
              </div>
              <div class="inq-field">
                <label>Travel Month <span class="inq-req">*</span></label>
                <select v-model="packageFilters.travel_month" class="inq-input">
                  <option v-for="(m, i) in pkgMonths" :key="i" :value="i + 1">{{ m }}</option>
                </select>
              </div>
            </div>
            <div class="inq-row">
              <div class="inq-field">
                <label>Travel Week <span class="inq-req">*</span></label>
                <select v-model="packageFilters.travel_week" class="inq-input">
                  <option v-for="w in pkgWeeks" :key="w" :value="w">{{ getWeekOrdinal(w) }} Week</option>
                </select>
              </div>
              <div class="inq-field">
                <label>Nights <span class="inq-req">*</span></label>
                <input v-model.number="packageFilters.nights" type="number" min="1" class="inq-input" placeholder="Nights" />
                <small v-if="packageFilters.nights > 0" class="inq-nights">{{ getNightsDaysText(packageFilters.nights) }}</small>
              </div>
            </div>
            <div class="inq-row">
              <div class="inq-field">
                <label>Rooms <span class="inq-req">*</span></label>
                <div class="inq-stepper">
                  <button type="button" :disabled="Number(packageFilters.room_qty) <= 1" @click="incrPackageGuest('room_qty', -1, 1)">&minus;</button>
                  <span>{{ packageFilters.room_qty }}</span>
                  <button type="button" @click="incrPackageGuest('room_qty', 1, 1)">+</button>
                </div>
              </div>
              <div class="inq-field">
                <label>Adults <span class="inq-req">*</span></label>
                <div class="inq-stepper">
                  <button type="button" :disabled="Number(packageFilters.adult_qty) <= 1" @click="incrPackageGuest('adult_qty', -1, 1)">&minus;</button>
                  <span>{{ packageFilters.adult_qty }}</span>
                  <button type="button" @click="incrPackageGuest('adult_qty', 1, 1)">+</button>
                </div>
              </div>
              <div class="inq-field">
                <label>Children</label>
                <div class="inq-stepper">
                  <button type="button" :disabled="Number(packageFilters.child_qty) <= 0" @click="incrPackageGuest('child_qty', -1, 0)">&minus;</button>
                  <span>{{ packageFilters.child_qty }}</span>
                  <button type="button" @click="incrPackageGuest('child_qty', 1, 0)">+</button>
                </div>
              </div>
              <div class="inq-field">
                <label>Infants</label>
                <div class="inq-stepper">
                  <button type="button" :disabled="Number(packageFilters.infant_qty) <= 0" @click="incrPackageGuest('infant_qty', -1, 0)">&minus;</button>
                  <span>{{ packageFilters.infant_qty }}</span>
                  <button type="button" @click="incrPackageGuest('infant_qty', 1, 0)">+</button>
                </div>
              </div>
            </div>
            <div v-if="Number(packageFilters.child_qty) > 0" class="inq-field">
              <label>Child Ages (comma-separated)</label>
              <input v-model="packageFilters.child_age" type="text" class="inq-input" placeholder="e.g. 4, 8" />
            </div>
            <div class="inq-field">
              <label>Special Requirements</label>
              <textarea v-model="packageFilters.special_requirements" class="inq-input" rows="2" placeholder="Meals, Transport, Hotels, etc."></textarea>
            </div>
            <div v-if="packageFormError" class="inq-error">{{ packageFormError }}</div>
            <div class="inq-footer">
              <button type="button" class="inq-btn-cancel" @click="closePackagePopup">Cancel</button>
              <button type="submit" class="inq-btn-submit" :disabled="sendingMessage">
                <i class="fas fa-paper-plane"></i> Send Package Request
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- Hidden file input -->
    <input
      ref="fileInputRef"
      type="file"
      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.zip"
      style="display:none"
      @change="onFileSelected"
    />

    <div v-if="!isMinimized" id="chatWidget" class="chat-widget">
      <div class="chat-header">
        <div class="chat-header-left">
          <button v-if="showBackButton" class="icon-btn" @click="currentView === 'history' ? closeHistoryToMessages() : backToCategories()">
            <i class="fas fa-arrow-left"></i>
          </button>
          <div>
            <h5 class="chat-title">{{ headerTitle }}</h5>
            <small class="chat-subtitle">
              <template v-if="currentView === 'messages'">
                <span :class="['status-dot', adminOnline ? 'online' : 'offline']"></span>
                {{ adminOnline ? 'Online' : 'Offline' }}
              </template>
              <template v-else>{{ headerSubtitle }}</template>
            </small>
          </div>
        </div>

        <div class="chat-header-actions">
          <button v-if="showHistoryButton" class="icon-btn" @click="openHistory">
            <i class="fas fa-history"></i>
          </button>
          <button class="icon-btn" @click="minimize">
            <i class="fas fa-minus"></i>
          </button>
        </div>
      </div>

      <div class="chat-content">
        <div v-if="loading" class="chat-empty">Initializing chat...</div>
        <div v-else-if="errorText" class="chat-empty text-danger">{{ errorText }}</div>

        <template v-else>
          <div v-if="currentView === 'category'" class="chat-category-view">
            <p class="category-help">Select a category:</p>
            <button v-for="item in categories" :key="item.key" class="category-btn" @click="selectCategory(item.key)">
              <span>{{ item.icon }}</span>
              <span>{{ item.label }}</span>
            </button>
          </div>

          <div v-else-if="currentView === 'history'" class="chat-history-view">
            <div v-if="loadingHistory" class="chat-empty">Loading conversations...</div>
            <div v-else-if="historyItems.length === 0" class="chat-empty">No conversations yet</div>
            <div v-else class="history-list">
              <button
                v-for="item in historyItems"
                :key="item.id"
                class="history-item"
                :disabled="item.status === 'closed'"
                @click="openChat(item.id)"
              >
                <div class="history-top">
                  <strong>Chat #{{ item.id.slice(0, 8) }}</strong>
                  <small>{{ formatTime(item.updated_at) }}</small>
                </div>
                <small class="history-last">{{ item.last_message || 'No messages' }}</small>
              </button>
            </div>
          </div>

          <div v-else class="chat-messages-view" @click="showAttachMenu = showAttachMenu!">
            <!-- Offline banner -->
            <div v-if="!adminOnline" class="offline-banner">
              ⚫ Support is offline — leave a message and we'll reply soon.
            </div>

            <div ref="messagesContainer" class="chat-messages" id="chatMessages">
              <div v-if="loadingMessages" class="chat-empty">Loading...</div>
              <div v-else-if="messages.length === 0" class="chat-empty">No messages yet</div>

              <template
                v-for="msg in messages"
                :key="msg.id || msg.message_id"
              >
                <!-- System / event notice — centred, no bubble, no context menu -->
                <div
                  v-if="msg.message_type === 'system' || msg.sender_role === 'system'"
                  :id="`msg-${msg.id || msg.message_id}`"
                  class="chat-msg-system"
                >
                  <span>{{ msg.message }}</span>
                </div>

                <!-- Normal message bubble -->
                <div
                  v-else
                  :id="`msg-${msg.id || msg.message_id}`"
                  :class="[
                    msg.sender_firebase_uid === firebaseUid ? 'chat-msg-own' : 'chat-msg-other',
                    highlightedMessageId === (msg.id || msg.message_id) ? 'chat-msg-highlighted' : ''
                  ]"
                  @contextmenu.prevent="onMsgContextMenu($event, msg)"
                  @touchstart.passive="onMsgTouchStart($event, msg)"
                  @touchend.passive="onMsgTouchEnd"
                  @touchmove.passive="onMsgTouchEnd"
                >
                  <div class="chat-msg-inner">
                    <small v-if="msg.sender_firebase_uid !== firebaseUid" class="sender-name">{{ msg.sender_name || 'Support' }}</small>
                    <div :class="msg.sender_firebase_uid === firebaseUid ? 'chat-bubble chat-bubble-own' : 'chat-bubble chat-bubble-other'">
                      <!-- Reply quote -->
                      <button
                        v-if="msg.reply_to"
                        type="button"
                        class="reply-quote"
                        @click.stop="scrollToMessage(msg.reply_to.message_id)"
                      >
                        <span class="reply-quote-name">{{ msg.reply_to.sender_name }}</span>
                        <span class="reply-quote-text">{{ msg.reply_to.message }}</span>
                      </button>
                      <!-- Deleted -->
                      <em v-if="msg.deleted" class="deleted-msg">🚫 Message deleted</em>
                      <!-- Inquiry Link -->
                      <template v-else-if="msg.message_type === 'inquiry_link' || msg.inquiry_link">
                        <button
                          type="button"
                          class="inquiry-link-btn"
                          @click="openInquiryPopup"
                        >
                          <i class="fas fa-hotel"></i> Open Hotel Inquiry Form
                        </button>
                      </template>
                      <!-- Package Request Link -->
                      <template v-else-if="msg.message_type === 'package_request_link' || msg.package_request_link">
                        <button
                          type="button"
                          class="inquiry-link-btn"
                          @click="openPackagePopup"
                        >
                          <i class="fas fa-suitcase"></i> Open Package Request Form
                        </button>
                      </template>
                      <!-- Image -->
                      <template v-else-if="msg.file_type === 'image'">
                        <img :src="msg.file_url" class="chat-img" loading="lazy" @click="openImagePreview(msg.file_url)" />
                      </template>
                      <!-- File -->
                      <template v-else-if="msg.file_type === 'file'">
                        <button
                          type="button"
                          class="chat-file-link"
                          @click="downloadFile(msg.file_url, msg.file_name || msg.message)"
                        >
                          <i class="fas fa-paperclip"></i> {{ msg.file_name || 'File' }}
                          <i class="fas fa-download"></i>
                        </button>
                      </template>
                      <!-- Text -->
                      <template v-else>{{ msg.message }}</template>
                    </div>
                    <small class="time-text">{{ formatTime(msg.created_at) }}</small>
                  </div>
                  <button class="msg-menu-btn" type="button" @click.stop="showCtxMenu($event.clientX, $event.clientY, msg)">
                    <i class="fas fa-ellipsis-v"></i>
                  </button>
                </div>
              </template>
            </div>

            <!-- Reply preview bar -->
            <div v-if="replyTo" class="reply-bar">
              <div class="reply-bar-content">
                <span class="reply-bar-name">{{ replyTo.sender_name }}</span>
                <span class="reply-bar-text">{{ replyTo.message }}</span>
              </div>
              <button class="reply-bar-close" @click="replyTo = null">×</button>
            </div>

            <!-- Slide-up attachment menu -->
            <Transition name="slide-up">
              <div v-if="showAttachMenu" class="attach-menu" @click.stop>
                <button type="button" class="attach-menu-item" :disabled="uploadingFile || sendingMessage" @click="fileInputRef?.click(); showAttachMenu = false" title="Attach File">
                  <i class="fas fa-paperclip"></i>
                  <span>Attach File</span>
                </button>
                <button type="button" class="attach-menu-item" :disabled="uploadingFile || sendingMessage" @click="openInquiryPopup(); showAttachMenu = false" title="Hotel Inquiry Form">
                  <i class="fas fa-hotel"></i>
                  <span>Hotel Inquiry</span>
                </button>
                <button type="button" class="attach-menu-item" :disabled="uploadingFile || sendingMessage" @click="openPackagePopup(); showAttachMenu = false" title="Package Request Form">
                  <i class="fas fa-suitcase"></i>
                  <span>Package Request</span>
                </button>
              </div>
            </Transition>

            <div class="chat-input-wrap">
              <button class="attach-btn" :disabled="uploadingFile || sendingMessage" @click="showAttachMenu = !showAttachMenu" title="Options">
                <i v-if="uploadingFile" class="fas fa-spinner fa-spin"></i>
                <i v-else :class="['fas', showAttachMenu ? 'fa-chevron-down' : 'fa-ellipsis-v']"></i>
              </button>
              <textarea
                id="chatMessageInput"
                ref="textareaRef"
                v-model="messageInput"
                placeholder="Type message..."
                rows="1"
                @keydown="onInputKeyDown"
                @input="autoResize"
              ></textarea>
              <button id="chatSendBtn" :disabled="sendingMessage || uploadingFile || !messageInput.trim()" @click="sendMessage">
                <i class="fas fa-paper-plane"></i>
              </button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <button v-if="isMinimized" id="chatBadge" class="chat-badge" @click="maximize">
      💬
    </button>
  </div>
</template>

<style scoped>
.chat-widget {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 380px;
  max-height: 600px;
  background: white;
  border-radius: 5px;
  box-shadow: 0 5px 40px rgba(0, 0, 0, 0.16);
  display: flex;
  flex-direction: column;
  z-index: 9999;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
}

.chat-header {
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
  color: white;
  padding: 16px;
  border-radius: 12px 12px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chat-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.chat-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.chat-subtitle {
  opacity: 0.8;
  font-size: 12px;
}

.chat-header-actions {
  display: flex;
  gap: 8px;
}

.icon-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
}

.chat-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 400px;
}

.chat-category-view {
  padding: 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.category-help {
  font-size: 13px;
  color: #666;
  margin: 0 0 8px;
  font-weight: 500;
}

.category-btn {
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  transition: all 0.2s;
  display: flex;
  gap: 8px;
  align-items: center;
}

.chat-history-view {
  flex: 1;
  overflow: hidden;
  background: #f9f9f9;
}

.history-list {
  max-height: 400px;
  overflow-y: auto;
}

.history-item {
  width: 100%;
  text-align: left;
  padding: 12px;
  border: 0;
  border-bottom: 1px solid #e0e0e0;
  background: white;
  cursor: pointer;
}

.history-item:disabled {
  opacity: 0.6;
  cursor: default;
}

.history-top {
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 4px;
}

.history-top small {
  font-size: 11px;
  color: #999;
}

.history-last {
  font-size: 12px;
  color: #666;
  display: block;
}

.chat-messages-view {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 400px;
  flex: 1;
  min-height: 0;
  position: relative;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chat-msg-own {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
}

.chat-msg-other {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 6px;
}

.chat-msg-inner {
  max-width: 100%;
}

.msg-menu-btn {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  flex-shrink: 0;
}

.msg-menu-btn:hover {
  background: rgba(148, 163, 184, 0.2);
}

.chat-msg-highlighted .chat-bubble {
  box-shadow: 0 0 0 2px #f59e0b;
  animation: msgFlash 1.2s ease;
}

@keyframes msgFlash {
  0% { transform: scale(1); }
  30% { transform: scale(1.015); }
  100% { transform: scale(1); }
}

.chat-bubble {
  padding: 8px 12px;
  border-radius: 5px;
  font-size: 13px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}

.chat-bubble-own {
  background: #2563eb;
  color: white;
  border-bottom-right-radius: 4px;
}

.chat-bubble-other {
  background: #e9ecef;
  color: #333;
  border-bottom-left-radius: 4px;
}

.sender-name {
  font-size: 11px;
  font-weight: 600;
  color: #2563eb;
  margin-bottom: 4px;
  display: block;
  margin-left: 4px;
}

.time-text {
  font-size: 11px;
  color: #999;
  margin-top: 2px;
  display: block;
  margin-left: 4px;
}

.chat-input-wrap {
  padding: 10px 12px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  align-items: flex-end;
  gap: 6px;
}

.chat-input-wrap textarea {
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 14px;
  padding: 8px 12px;
  font-size: 13px;
  outline: none;
  resize: none;
  max-height: 120px;
  min-height: 36px;
  height: 36px;
  overflow-y: auto;
  line-height: 1.4;
  transition: height 0.1s ease;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.chat-input-wrap button {
  background: #2563eb;
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  flex-shrink: 0;
}

.chat-input-wrap button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Slide-up menu */
.attach-menu {
  position: absolute;
  bottom: 60px;
  left: 16px;
  right: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 -2px 16px rgba(0, 0, 0, 0.15);
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 10;
}

.attach-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #333;
  transition: background 0.2s;
  text-align: left;
}

.attach-menu-item:hover:not(:disabled) {
  background: #f5f5f5;
}

.attach-menu-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.attach-menu-item i {
  font-size: 18px;
  width: 24px;
  text-align: center;
  color: #2563eb;
}

.attach-menu-item span {
  flex: 1;
  font-weight: 500;
}

/* Slide-up animation */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.slide-up-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.slide-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.chat-empty {
  text-align: center;
  color: #999;
  padding: 20px;
}

.chat-badge {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 9998;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  color: white;
  font-size: 24px;
  transition: all 0.3s;
  border: none;
}

@media (max-width: 768px) {
  .chat-widget {
    bottom: 0;
    right: 0;
    left: 0;
    top: 0;
    width: 100%;
    max-height: 100dvh;
    height: 100dvh;
    border-radius: 0;
    max-width: 100%;
    padding-bottom: env(safe-area-inset-bottom);
  }

  .chat-header {
    border-radius: 0;
    padding-top: calc(12px + env(safe-area-inset-top));
  }

  .chat-content {
    min-height: 0;
  }

  .chat-messages-view {
    max-height: none;
    min-height: 0;
    flex: 1;
  }

  .chat-messages {
    min-height: 0;
  }

  .chat-input-wrap {
    padding: 8px 10px calc(8px + env(safe-area-inset-bottom));
    gap: 8px;
  }

  .chat-input-wrap textarea {
    font-size: 16px;
    max-height: 140px;
  }

  .chat-input-wrap button {
    width: 36px;
    height: 36px;
    min-width: 36px;
  }

  .chat-badge {
    width: 60px;
    height: 60px;
    font-size: 28px;
    bottom: calc(20px + env(safe-area-inset-bottom));
  }
}

/* ── New feature styles ─────────────────────────────────── */
.status-dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; margin-right: 4px; vertical-align: middle; }
.status-dot.online { background: #22c55e; }
.status-dot.offline { background: #94a3b8; }
.offline-banner { background: #f1f5f9; border-bottom: 1px solid #e2e8f0; padding: 6px 12px; font-size: 11px; color: #64748b; text-align: center; }
.reply-quote { border-left: 3px solid rgba(255,255,255,0.5); padding: 4px 8px; margin-bottom: 6px; border-radius: 3px; background: rgba(0,0,0,0.08); display: block; width: 100%; border-top: none; border-right: none; border-bottom: none; text-align: left; color: inherit; cursor: pointer; }
.chat-bubble-other .reply-quote { border-left-color: #2563eb; background: rgba(37,99,235,0.08); }
.reply-quote-name { display: block; font-size: 10px; font-weight: 700; margin-bottom: 2px; }
.reply-quote-text { display: block; font-size: 11px; opacity: 0.85; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 220px; }
.deleted-msg { font-size: 12px; opacity: 0.6; }
.chat-img { max-width: 200px; max-height: 200px; border-radius: 6px; display: block; cursor: pointer; }
.chat-file-link { display: flex; align-items: center; gap: 6px; font-size: 12px; text-decoration: underline; color: inherit; word-break: break-all; background: none; border: none; padding: 0; cursor: pointer; }
.reply-bar { display: flex; align-items: center; justify-content: space-between; padding: 6px 12px; background: #eff6ff; border-top: 1px solid #bfdbfe; font-size: 12px; }
.reply-bar-content { flex: 1; overflow: hidden; }
.reply-bar-name { display: block; font-weight: 700; color: #2563eb; font-size: 11px; }
.reply-bar-text { display: block; color: #374151; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.reply-bar-close { background: none; border: none; font-size: 18px; color: #94a3b8; cursor: pointer; padding: 0 4px; line-height: 1; }
.attach-btn { background: none; border: none; color: #6b7280; cursor: pointer; padding: 0 4px; font-size: 16px; display: flex; align-items: center; transition: transform 0.2s; }
.attach-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.attach-btn i { transition: transform 0.2s; }
.inquiry-open-btn { color: #2563eb; }
.package-open-btn { color: #7c3aed; }
.chat-msg-system { align-self: center; text-align: center; font-size: 11px; color: #64748b; background: #f1f5f9; border-radius: 10px; padding: 4px 14px; margin: 4px 0; max-width: 90%; font-style: italic; }
.inquiry-link-btn { display: flex; align-items: center; gap: 6px; padding: 8px 14px; border: 2px solid; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; background: transparent; }
.chat-bubble-own .inquiry-link-btn { border-color: rgba(255,255,255,0.8); color: white; }
.chat-bubble-own .inquiry-link-btn:hover { background: rgba(255,255,255,0.15); }
.chat-bubble-other .inquiry-link-btn { border-color: #2563eb; color: #2563eb; }
.chat-bubble-other .inquiry-link-btn:hover { background: rgba(37,99,235,0.08); }

.chat-image-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.78);
  z-index: 100001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 22px;
}

.chat-image-modal-img {
  max-width: min(95vw, 1100px);
  max-height: 90vh;
  width: auto;
  height: auto;
  border-radius: 10px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.45);
}

.chat-image-modal-close {
  position: fixed;
  top: 16px;
  right: 18px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.chat-image-modal-close:hover {
  background: rgba(255, 255, 255, 0.32);
}
</style>

<style>
/* Context menu lives outside scoped component (Teleport) */
.chat-ctx-menu { position: fixed; z-index: 99999; background: white; border: 1px solid #e5e7eb; border-radius: 8px; box-shadow: 0 4px 16px rgba(0,0,0,0.12); min-width: 130px; padding: 4px 0; }
.ctx-item { display: flex; align-items: center; gap: 8px; width: 100%; padding: 8px 14px; border: none; background: none; cursor: pointer; font-size: 13px; color: #374151; text-align: left; }
.ctx-item:hover { background: #f3f4f6; }
.ctx-delete { color: #ef4444; }
.ctx-delete:hover { background: #fef2f2; }

/* ── Hotel Inquiry Popup ────────────────────────────────────────────────────── */
.chat-inquiry-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.52); z-index: 100002; display: flex; align-items: center; justify-content: center; padding: 16px; }
.chat-inquiry-popup { background: white; border-radius: 12px; box-shadow: 0 8px 32px rgba(0,0,0,0.22); width: 100%; max-width: 460px; max-height: 90vh; display: flex; flex-direction: column; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif; }
.chat-inquiry-header { background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%); color: white; padding: 14px 16px; border-radius: 12px 12px 0 0; display: flex; align-items: center; justify-content: space-between; font-weight: 600; font-size: 14px; }
.chat-inquiry-close { background: rgba(255,255,255,0.2); border: none; color: white; width: 28px; height: 28px; border-radius: 50%; cursor: pointer; font-size: 20px; line-height: 1; display: flex; align-items: center; justify-content: center; }
.chat-inquiry-close:hover { background: rgba(255,255,255,0.32); }
.chat-inquiry-body { padding: 14px 16px; overflow-y: auto; display: flex; flex-direction: column; gap: 10px; }
.inq-field { display: flex; flex-direction: column; gap: 3px; }
.inq-field label { font-size: 12px; font-weight: 600; color: #374151; }
.inq-req { color: #ef4444; }
.inq-input { border: 1px solid #d1d5db; border-radius: 6px; padding: 7px 10px; font-size: 13px; outline: none; width: 100%; box-sizing: border-box; transition: border-color 0.15s; font-family: inherit; }
.inq-input:focus { border-color: #2563eb; }
.inq-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(100px, 1fr)); gap: 8px; }
.inq-nights { font-size: 12px; color: #2563eb; font-weight: 600; text-align: center; background: #eff6ff; border-radius: 4px; padding: 4px 8px; }
.inq-stepper { display: flex; align-items: center; gap: 8px; background: #f9fafb; border: 1px solid #d1d5db; border-radius: 6px; padding: 5px 10px; }
.inq-stepper button { background: #e5e7eb; border: none; width: 22px; height: 22px; border-radius: 50%; cursor: pointer; font-size: 13px; display: flex; align-items: center; justify-content: center; padding: 0; }
.inq-stepper button:disabled { opacity: 0.4; cursor: not-allowed; }
.inq-stepper span { min-width: 18px; text-align: center; font-size: 13px; font-weight: 600; }
.inq-budget-opts { display: flex; gap: 16px; }
.inq-radio { display: flex; align-items: center; gap: 5px; font-size: 13px; cursor: pointer; color: #374151; }
.inq-error { color: #ef4444; font-size: 12px; background: #fef2f2; border-radius: 4px; padding: 6px 10px; }
.inq-footer { display: flex; gap: 8px; justify-content: flex-end; padding-top: 4px; }
.inq-btn-cancel { padding: 7px 16px; border: 1px solid #d1d5db; background: white; border-radius: 6px; font-size: 13px; cursor: pointer; color: #374151; }
.inq-btn-cancel:hover { background: #f3f4f6; }
.inq-btn-submit { padding: 7px 16px; background: #2563eb; color: white; border: none; border-radius: 6px; font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.inq-btn-submit:not(:disabled):hover { background: #1d4ed8; }
.inq-btn-submit:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Booking.com-style Calendar & Guest Picker (for Hotel Inquiry Popup) ────────────── */
/* Date bar trigger */
.bk-date-bar {
  display: flex;
  align-items: stretch;
  border: 1.5px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  user-select: none;
  overflow: hidden;
  min-height: 44px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.bk-date-bar:hover {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}
.bk-date-section {
  flex: 1;
  padding: 8px 14px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.bk-date-section + .bk-date-section {
  border-left: 1px solid #e5e7eb;
}
.bk-nights-section {
  padding: 8px 14px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.82rem;
  color: #6b7280;
  font-weight: 500;
  white-space: nowrap;
  border-left: 1px solid #e5e7eb;
  border-right: 1px solid #e5e7eb;
  min-width: 72px;
}
.bk-date-label {
  font-size: 0.72rem;
  font-weight: 700;
  color: #6b7280;
  display: block;
  letter-spacing: 0.01em;
  margin-bottom: 2px;
}
.bk-date-value {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1a3052;
}
.bk-date-placeholder {
  font-size: 0.8rem;
  font-weight: 400;
  color: #9ca3af;
}

/* Calendar popup - fixed-position via Teleport */
.bk-cal-popup {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  padding: 16px 20px 12px;
  border: 1px solid #e5e7eb;
}
.bk-cal-nav {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  gap: 8px;
}
.bk-cal-nav-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1.5px solid #d1d5db;
  background: #fff;
  color: #374151;
  font-size: 1.2rem;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.1s, border-color 0.1s;
}
.bk-cal-nav-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}
.bk-cal-month-names {
  flex: 1;
  display: flex;
}
.bk-cal-month-names strong {
  flex: 1;
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
  text-align: center;
}
.bk-cal-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}
.bk-cal-dow {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  text-align: center;
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f3f4f6;
}
.bk-weekend {
  color: #2563eb;
}
.bk-cal-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}
.bk-cal-day {
  position: relative;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  color: #111827;
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.08s;
  margin: 1px 0;
}
.bk-day-num {
  position: relative;
  z-index: 2;
  pointer-events: none;
}
.bk-cal-day:not(.past):not(.empty):not(.start):not(.end):not(.range):not(.hover-end):not(.hover-range):hover {
  background: #e0eafe;
  border-radius: 50%;
}
.bk-cal-day.start {
  background: #2563eb;
  color: #fff;
  font-weight: 700;
  border-radius: 50% 0 0 50%;
}
.bk-cal-day.end {
  background: #2563eb;
  color: #fff;
  font-weight: 700;
  border-radius: 0 50% 50% 0;
}
.bk-cal-day.start.end {
  border-radius: 50%;
}
.bk-cal-day.range {
  background: #dbeafe;
  color: #1e40af;
  border-radius: 0;
}
.bk-cal-day.hover-end {
  background: #bfdbfe;
  color: #1e40af;
  font-weight: 600;
  border-radius: 0 50% 50% 0;
}
.bk-cal-day.hover-range {
  background: #eff6ff;
  color: #1e40af;
  border-radius: 0;
}
.bk-cal-day.past {
  color: #d1d5db;
  cursor: default;
}
.bk-cal-day.empty {
  pointer-events: none;
  background: none;
}

/* Hover tooltip */
.bk-cal-tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #1e293b;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 500;
  white-space: nowrap;
  padding: 4px 9px;
  border-radius: 4px;
  pointer-events: none;
  z-index: 10;
}
.bk-cal-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 4px solid transparent;
  border-top-color: #1e293b;
}

/* Calendar bottom bar */
.bk-cal-bottom {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #f3f4f6;
}
.bk-cal-holiday-note {
  font-size: 0.75rem;
  color: #9ca3af;
}
.bk-cal-range-text {
  font-size: 0.82rem;
  color: #374151;
  text-align: right;
}
.bk-cal-tz-note {
  font-size: 0.7rem;
  color: #9ca3af;
  margin-top: 2px;
  display: block;
}

/* Guest bar trigger */
.bk-guest-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1.5px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  padding: 6px 14px;
  min-height: 38px;
  user-select: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.bk-guest-bar:hover {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}
.bk-guest-icon {
  color: #6b7280;
  flex-shrink: 0;
  font-size: 1rem;
}
.bk-guest-summary {
  flex: 1;
  font-size: 0.8rem;
  color: #1a3052;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bk-guest-caret {
  color: #6b7280;
  font-size: 0.8rem;
  flex-shrink: 0;
}

/* Guest popup - fixed-position via Teleport */
.bk-guest-popup {
  position: fixed;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  padding: 8px 20px 16px;
  border: 1px solid #e5e7eb;
  z-index: 100003;
}
.bk-guest-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
}
.bk-guest-row:last-of-type { border-bottom: none; }
.bk-guest-label-wrap { flex: 1; }
.bk-guest-name {
  font-size: 0.8rem;
  font-weight: 500;
  color: #111827;
}
.bk-guest-hint {
  font-size: 0.75rem;
  color: #9ca3af;
  margin-top: 1px;
}
.bk-guest-ctrl {
  display: flex;
  align-items: center;
  gap: 16px;
}
.bk-guest-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px solid #2563eb;
  background: #fff;
  color: #2563eb;
  font-size: 1.1rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.1s;
}
.bk-guest-btn:hover:not(:disabled) { background: #dbeafe; }
.bk-guest-btn:disabled {
  border-color: #d1d5db;
  color: #d1d5db;
  cursor: default;
}
.bk-guest-num {
  font-size: 0.8rem;
  font-weight: 500;
  color: #111827;
  min-width: 24px;
  text-align: center;
}
.bk-guest-child-ages { padding: 8px 0 4px; }
.bk-done-btn {
  display: block;
  width: 100%;
  margin-top: 12px;
  background: #2563eb;
  border-color: #2563eb;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 6px;
  color: white;
  cursor: pointer;
}
.bk-done-btn:hover { background: #1d4ed8; border-color: #1d4ed8; }

/* Small form-control used in guest picker child ages */
.form-control-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.82rem;
  border-radius: 0.375rem;
  border: 1px solid #d1d5db;
}
.form-control-sm:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 0.2rem rgba(37, 99, 235, 0.25);
}
</style>
